import React, { useState, useEffect } from 'react';
import { Button } from '@common/Button';
import { Input, Select, TextArea } from '@common/FormFields';
import { Edit2, Save, X } from 'lucide-react';
import { VEHICLE_TYPES } from '@utils/constants/vehicleTypes';
import { logError, logInfo } from '@utils/logger';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@config/firebase';
import { Toast } from '@common/Toast';

export const SurveyDetailsTab = ({ survey, editing = false }) => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    name: survey?.name || '',
    customerName: survey?.customerName || '',
    customerPO: survey?.customerPO || '',
    surveyType: survey?.surveyType || '',
    surveyMadeBy: survey?.surveyMadeBy || '',
    surveyValidatedBy: survey?.surveyValidatedBy || '',
    transportCompany: survey?.transportCompany || '',
    mapDetails: survey?.mapDetails || '',
    challengesIdentified: survey?.challengesIdentified || '',
    stakeholderFeedback: survey?.stakeholderFeedback || '',
    notes: survey?.notes || '',
    origin: survey?.origin || '',
    destination: survey?.destination || '',
    vehicle: {
      type: survey?.vehicle?.type || '',
      weight: survey?.vehicle?.weight || '',
      weightUnit: survey?.vehicle?.weightUnit || 'lbs',
      length: survey?.vehicle?.length || '',
      width: survey?.vehicle?.width || '',
      height: survey?.vehicle?.height || ''
    },
    highPoleHeights: {
      required: survey?.highPoleHeights?.required || false,
      value: survey?.highPoleHeights?.value || '',
      unit: survey?.highPoleHeights?.unit || 'ft'
    }
  });

  // Update form data when survey changes
  useEffect(() => {
    if (survey) {
      setFormData({
        name: survey.name || '',
        customerName: survey.customerName || '',
        customerPO: survey.customerPO || '',
        surveyType: survey.surveyType || '',
        surveyMadeBy: survey.surveyMadeBy || '',
        surveyValidatedBy: survey.surveyValidatedBy || '',
        transportCompany: survey.transportCompany || '',
        mapDetails: survey.mapDetails || '',
        challengesIdentified: survey.challengesIdentified || '',
        stakeholderFeedback: survey.stakeholderFeedback || '',
        notes: survey.notes || '',
        origin: survey.origin || '',
        destination: survey.destination || '',
        vehicle: {
          type: survey.vehicle?.type || '',
          weight: survey.vehicle?.weight || '',
          weightUnit: survey.vehicle?.weightUnit || 'lbs',
          length: survey.vehicle?.length || '',
          width: survey.vehicle?.width || '',
          height: survey.vehicle?.height || ''
        },
        highPoleHeights: {
          required: survey.highPoleHeights?.required || false,
          value: survey.highPoleHeights?.value || '',
          unit: survey.highPoleHeights?.unit || 'ft'
        }
      });
    }
  }, [survey]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.name?.trim()) {
        throw new Error('Survey name is required');
      }
      if (!formData.customerName?.trim()) {
        throw new Error('Customer name is required');
      }
      if (!formData.vehicle?.type) {
        throw new Error('Vehicle type is required');
      }

      // Update survey in Firestore
      const surveyRef = doc(db, 'surveys', survey.id);
      await updateDoc(surveyRef, {
        ...formData,
        updatedAt: new Date().toISOString()
      });

      setEditing(false);
      setToast({
        type: 'success',
        message: 'Survey details updated successfully'
      });
      logInfo('Survey details updated successfully');
    } catch (error) {
      logError('Failed to update survey:', error);
      setToast({
        type: 'error',
        message: error.message || 'Failed to update survey'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!survey) {
    return (
      <div className="text-center py-8 text-gray-400">
        No survey selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Survey Name */}
      <Input
        label="Survey Name"
        value={survey?.name || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
      />

      {/* Customer Name */}
      <Input
        label="Customer Name"
        value={survey?.customerName || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
      />

      {/* Customer PO */}
      <Input
        label="Customer PO"
        value={survey?.customerPO || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
      />

      {/* Map Details */}
      <TextArea
        label="Map Details"
        value={survey?.mapDetails || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
        rows={4}
      />

      {/* Survey Type */}
      <Select
        label="Survey Type"
        value={survey?.surveyType || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
        options={[
          { value: 'book_survey', label: 'Book Survey' },
          { value: 'quick_survey', label: 'Quick Survey' },
          { value: 'high_pole', label: 'High Pole Survey' },
          { value: 'advanced', label: 'Advanced Survey' }
        ]}
      />

      {/* Survey Made By & Validated By */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Survey Made By"
          value={survey?.surveyMadeBy || ''}
          readOnly={!editing}
          className="bg-gray-700/50 text-white"
          labelClassName="text-gray-300"
        />
        <Input
          label="Survey Validated By"
          value={survey?.surveyValidatedBy || ''}
          readOnly={!editing}
          className="bg-gray-700/50 text-white"
          labelClassName="text-gray-300"
        />
      </div>

      {/* Transport Company */}
      <Input
        label="Transport Company"
        value={survey?.transportCompany || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
      />

      {/* Challenges Identified */}
      <TextArea
        label="Challenges Identified"
        value={survey?.challengesIdentified || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
        rows={4}
      />

      {/* Stakeholder Feedback */}
      <TextArea
        label="Stakeholder Feedback"
        value={survey?.stakeholderFeedback || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
        rows={4}
      />

      {/* Notes */}
      <TextArea
        label="Notes"
        value={survey?.notes || ''}
        readOnly={!editing}
        className="bg-gray-700/50 text-white"
        labelClassName="text-gray-300"
        rows={4}
      />

      {/* Route Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Origin"
          value={survey?.origin || ''}
          readOnly={!editing}
          className="bg-gray-700/50 text-white"
          labelClassName="text-gray-300"
        />
        <Input
          label="Destination"
          value={survey?.destination || ''}
          readOnly={!editing}
          className="bg-gray-700/50 text-white"
          labelClassName="text-gray-300"
        />
      </div>

      {/* Vehicle Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Vehicle Information</h3>
        
        <Select
          label="Vehicle Type"
          value={survey?.vehicle?.type || ''}
          readOnly={!editing}
          className="bg-gray-700/50 text-white"
          labelClassName="text-gray-300"
          options={[
            { value: 'Flatbed', label: 'Flatbed' },
            { value: 'Step Deck', label: 'Step Deck' },
            { value: 'Double Drop', label: 'Double Drop' },
            { value: 'Extendable Trailers', label: 'Extendable Trailers' },
            { value: 'RGN LowBed', label: 'RGN LowBed' },
            { value: 'Mini Deck', label: 'Mini Deck' },
            { value: 'Jeep & Steering Dolly', label: 'Jeep & Steering Dolly' },
            { value: 'Single Schnabel', label: 'Single Schnabel' },
            { value: 'Double Schnabel', label: 'Double Schnabel' },
            { value: 'Hydraulic Platform', label: 'Hydraulic Platform' }
          ]}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Weight"
            value={survey?.vehicle?.weight || ''}
            readOnly={!editing}
            className="bg-gray-700/50 text-white"
            labelClassName="text-gray-300"
          />
          <Select
            label="Weight Unit"
            value={survey?.vehicle?.weightUnit || 'lbs'}
            readOnly={!editing}
            className="bg-gray-700/50 text-white"
            labelClassName="text-gray-300"
            options={[
              { value: 'lbs', label: 'lbs' },
              { value: 'kg', label: 'kg' }
            ]}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Input
            label="Length (ft)"
            value={survey?.vehicle?.length || ''}
            readOnly={!editing}
            className="bg-gray-700/50 text-white"
            labelClassName="text-gray-300"
          />
          <Input
            label="Width (ft)"
            value={survey?.vehicle?.width || ''}
            readOnly={!editing}
            className="bg-gray-700/50 text-white"
            labelClassName="text-gray-300"
          />
          <Input
            label="Height (ft)"
            value={survey?.vehicle?.height || ''}
            readOnly={!editing}
            className="bg-gray-700/50 text-white"
            labelClassName="text-gray-300"
          />
        </div>
      </div>

      {/* High Pole Requirements */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">High Pole Requirements</h3>
        
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={survey?.highPoleHeights?.required || false}
            readOnly={!editing}
            className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
          />
          <label className="text-gray-300">Required</label>
        </div>

        {survey?.highPoleHeights?.required && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Height"
              value={survey?.highPoleHeights?.value || ''}
              readOnly={!editing}
              className="bg-gray-700/50 text-white"
              labelClassName="text-gray-300"
            />
            <Select
              label="Unit"
              value={survey?.highPoleHeights?.unit || 'ft'}
              readOnly={!editing}
              className="bg-gray-700/50 text-white"
              labelClassName="text-gray-300"
              options={[
                { value: 'ft', label: 'ft' },
                { value: 'm', label: 'm' }
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
};